import { HttpClient, HttpParams, HttpEvent, HttpEventType, HttpErrorResponse, HttpHeaders, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WebFamCitation, WebFamSource, WebFamSourceManage, WebFamCitationManage, WebFamNameTemplate,
         formatSourceText,
         citationEventTypeName } from '../models/source.model';
import { environment } from 'src/environments/environment';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { WebFamFile, WebFamUsages } from '../models/common.model';

@Injectable()
export class SourceService {
    // placeList: EventPlaceList[]; // = [{_id: 'test', count: 1}, {_id: 'test2', count: 3}];

    constructor(private httpClient: HttpClient) { }

    uploadFileSource(fileData: FormData, sourceId: string) {
        return this.httpClient.post<WebFamFile>(environment.backendUrl + '/source/upload/tree/'
                                            + environment.currentTree + '/source/' + sourceId,
            fileData, { reportProgress: true, observe: 'events' }).pipe(map(event => this.getEventMessage(event, fileData)),
                catchError(this.handleError)
            );
    }

    uploadFileCitation(fileData: FormData, sourceId: string, citationId: string) {
        return this.httpClient.post<WebFamFile>(
            environment.backendUrl + '/source/upload/tree/' + environment.currentTree
            + '/source/' + sourceId + '/citation/' + citationId,
                    fileData,
                    { reportProgress: true, observe: 'events' }
                ).
                pipe(map(event => this.getEventMessage(event, fileData)),
                    catchError(this.handleError)
                );
    }
    insertSource(input: unknown) {
        return this.httpClient.post<string>(environment.backendUrl + '/source/insert/tree/' + environment.currentTree, { data: input });
    }

    updateSource(input: WebFamSource) {
        return this.httpClient.patch<string>(
                    environment.backendUrl + '/source/update/tree/' + environment.currentTree + '/source/' + input.id,
                    { data: input }
                );
    }

    insertCitation(data: WebFamCitation) {
        return this.httpClient.post<WebFamCitation>(environment.backendUrl + '/citation/insert/tree/' + environment.currentTree, { data });
    }

    insertCitationFiles(citationId: string, input: string[]) {
        return this.httpClient.post<string>(
                    environment.backendUrl + '/citation/files/insert/tree/' + environment.currentTree + '/citation/' + citationId,
                    { data: input }
                );
    }

    removeCitationFiles(citationId: string, input: string[]) {
        const httpOptions = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: { data: input }
        };

        return this.httpClient.delete<string>(
                    environment.backendUrl + '/citation/files/remove/tree/' + environment.currentTree + '/citation/' + citationId,
                    httpOptions
                );
    }

    removeSourceFiles(sourceId: string, input: string[]) {
        const httpOptions = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: { data: input }
        };

        return this.httpClient.delete<string>(
                    environment.backendUrl + '/source/files/remove/tree/' + environment.currentTree + '/source/' + sourceId,
                    httpOptions
                );
    }

    updateCitation(input: WebFamCitation) {
        return this.httpClient.patch<WebFamCitation>(
                    environment.backendUrl + '/citation/update/tree/' + environment.currentTree + '/citation/' + input.id,
                    { data: input }
                );
    }

    /* --- szablony nazw ---------------------------------------------------- */

    getNameTemplates() {
        return this.httpClient.get<WebFamNameTemplate[]>(
                    environment.backendUrl + '/source/template/list/tree/' + environment.currentTree
                );
    }

    /** Bez id wstawia nowy szablon, z id aktualizuje istniejacy. */
    saveNameTemplate(template: Partial<WebFamNameTemplate>) {
        return this.httpClient.post<WebFamNameTemplate>(
                    environment.backendUrl + '/source/template/save/tree/' + environment.currentTree,
                    { data: template }
                );
    }

    removeNameTemplate(templateId: string) {
        return this.httpClient.delete<{ status: string }>(
                    environment.backendUrl + '/source/template/remove/tree/' + environment.currentTree
                    + '/template/' + templateId
                );
    }

    /* --- porzadkowanie zrodel i cytatow (ustawienia drzewa) --------------- */

    /** Wszystkie zrodla drzewa z licznikami cytatow, uzyc i plikow. */
    getSourceManageList() {
        return this.httpClient.get<WebFamSourceManage[]>(
                    environment.backendUrl + '/source/manage/list/tree/' + environment.currentTree
                );
    }

    /** Cytaty jednego zrodla z licznikami uzyc i plikow. */
    getCitationManageList(sourceId: string) {
        return this.httpClient.get<WebFamCitationManage[]>(
                    environment.backendUrl + '/citation/manage/list/tree/' + environment.currentTree
                    + '/source/' + sourceId
                );
    }

    /** Konkretne miejsca uzycia zrodla - zdarzenia i profile. */
    getSourceUsages(sourceId: string) {
        return this.httpClient.get<WebFamUsages>(
                    environment.backendUrl + '/source/manage/usages/tree/' + environment.currentTree + '/source/' + sourceId
                );
    }

    /** Konkretne miejsca uzycia cytatu - zdarzenia i profile. */
    getCitationUsages(citationId: string) {
        return this.httpClient.get<WebFamUsages>(
                    environment.backendUrl + '/citation/manage/usages/tree/' + environment.currentTree
                    + '/citation/' + citationId
                );
    }

    /** Scala zrodlo-duplikat z celem - cytaty, uzycia i pliki przechodza na cel. */
    mergeSources(sourceId: string, targetId: string) {
        return this.httpClient.post<{ status: string; message?: string; moved_usages?: number; moved_citations?: number }>(
                    environment.backendUrl + '/source/merge/tree/' + environment.currentTree
                    + '/source/' + sourceId + '/target/' + targetId, {}
                );
    }

    /** Scala cytat-duplikat z celem (moze byc z innego zrodla). */
    mergeCitations(citationId: string, targetId: string) {
        return this.httpClient.post<{ status: string; message?: string; moved_usages?: number; moved_files?: number }>(
                    environment.backendUrl + '/citation/merge/tree/' + environment.currentTree
                    + '/citation/' + citationId + '/target/' + targetId, {}
                );
    }

    /** Przenosi cytat do innego zrodla. */
    moveCitationToSource(citationId: string, sourceId: string) {
        return this.httpClient.patch<{ status: string; message?: string }>(
                    environment.backendUrl + '/citation/movesource/tree/' + environment.currentTree
                    + '/citation/' + citationId + '/source/' + sourceId, {}
                );
    }

    /** Usuwa zrodlo - backend odmawia, gdy ma cytaty lub uzycia. */
    removeSource(sourceId: string) {
        return this.httpClient.delete<{ status: string; message?: string }>(
                    environment.backendUrl + '/source/remove/tree/' + environment.currentTree + '/source/' + sourceId
                );
    }

    /** Usuwa cytat - backend odmawia, gdy jest uzywany. */
    removeCitation(citationId: string) {
        return this.httpClient.delete<{ status: string; message?: string }>(
                    environment.backendUrl + '/citation/remove/tree/' + environment.currentTree + '/citation/' + citationId
                );
    }

    getSource(id: string) {
        return this.httpClient.get<WebFamSource>(environment.backendUrl + '/source/get/tree/' + environment.currentTree + '/source/' + id);
    }

    getCitation(id: string) {
        return this.httpClient.get<WebFamCitation>(environment.backendUrl + '/citation/get/tree/' + environment.currentTree
            + '/citation/' + id);
    }

    getCitationFiles(id: string) {
        return this.httpClient.get<WebFamFile[]>(environment.backendUrl + '/citation/list/files/tree/' + environment.currentTree
            + '/citation/' + id);
    }

    /**
     * Prostokat (ulamki 0..1) wskazujacy miejsce aktu na zdjeciu cytatu;
     * null czysci zaznaczenie. Akt na kilku zdjeciach = po jednym prostokacie
     * na kazde przypiete zdjecie.
     */
    updateCitationFileRegion(citationId: string, fileId: string,
                             region: { x: number; y: number; w: number; h: number } | null) {
        const data = region ?? { x: null, y: null, w: null, h: null };
        return this.httpClient.patch<{ status: string }>(
                    environment.backendUrl + '/citation/files/region/tree/' + environment.currentTree
                    + '/citation/' + citationId + '/file/' + fileId,
                    { data }
                );
    }

    getSourceFiles(id: string) {
        return this.httpClient.get<WebFamFile[]>(environment.backendUrl + '/source/list/files/tree/' + environment.currentTree
            + '/source/' + id);
    }

    getSourceList(source: string) {
        let params = new HttpParams();
        params = params.append('skip', '0');
        params = params.append('limit', '5');

        return this.httpClient.get<WebFamSource[]>(
                    environment.backendUrl + '/source/list/tree/' + environment.currentTree + '/name/' + source,
                    { params }
                );
    }

    /**
     * Cytaty źródła. Domyślne 15 to podpowiedź przy pisaniu; lista do
     * przeglądania woła z większym limitem.
     */
    getCitationList(sourceId: string, citation: string, limit: number = 15) {
        let params = new HttpParams();
        params = params.append('skip', '0');
        params = params.append('limit', String(limit));

        return this.httpClient.get<WebFamCitation[]>(
            environment.backendUrl + '/citation/list/tree/' + environment.currentTree + '/source/' + sourceId
            + '/title/' + citation,
                    { params }
                );
    }

    /**
     * Opis źródła w podpowiedzi i w polu po wyborze. Sama nazwa nie wystarcza:
     * ta sama parafia ma osobne księgi dla różnych okresów, więc bez lat
     * pozycje na liście są nie do odróżnienia. Ten sam opis widać w przypisach
     * na profilu - stąd wspólny formatSourceText.
     */
    formatLookupSource(value?: WebFamSource, full?: boolean): string {
        return formatSourceText(value);
    }

    formatLookupCitation(value?: WebFamCitation, full?: boolean): string {
        let inData = '';
        if (value) {
            // akt metrykalny bez tytulu przedstawia sie rodzajem zdarzenia
            inData = value.title ? value.title : citationEventTypeName(value.type);
            if (value.year) {
                inData += ', rok ' + value.year;
            }
            if (value.pages) {
                inData += ', str. ' + value.pages;
            }

            return inData;
        }
        return inData;
    }

    private getEventMessage(event: HttpEvent<unknown>, formData: FormData) {
        switch (event.type) {
            case HttpEventType.UploadProgress:
                return this.fileUploadProgress(event);
            case HttpEventType.Response:
                return this.apiResponse(event);
        }
        return undefined;
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            console.error(`Backend returned code ${error.status}`);
        }
        // return an observable with a user-facing error message
        return throwError('Something bad happened. Please try again later.');
    }

    private fileUploadProgress(event: HttpProgressEvent) {
        const percentDone = Math.round(100 * event.loaded / (event.total ? event.total : 0));
        return { status: 'progress', message: percentDone };
    }

    private apiResponse(event: HttpResponse<unknown>) {
        return event.body;
    }

}
